/* eslint-disable react-hooks/exhaustive-deps */
// export default IncidentRequest;
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { CrimeReference, CrimeType, IncidentStatus, Roles } from "types";
import { ITEMS_PER_PAGE } from "utils/constant";
import { DateView, errorHandler, hasRole } from "utils/helper";
import PageHeader from "../../../components/PageHeader";
import {
  fetchCrimeReferencesByCoordinatorRolePaginated,
  fetchCrimeReferencesPaginated,
} from "../../../services/incidentService";

// Status-based filter tabs (label shown in the tab bar -> incident status key).
const STATUS_FILTERS: { label: string; value: string | null }[] = [
  { label: "All", value: null },
  { label: IncidentStatus.pending, value: "pending" },
  {
    label: IncidentStatus["forwarded to registrar"],
    value: "forwarded to registrar",
  },
  { label: IncidentStatus.sent_to_police, value: "sent_to_police" },
  { label: IncidentStatus.terminated, value: "terminated" },
];
const tabs = STATUS_FILTERS.map((f) => f.label);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      assigned_to_clerk: "bg-blue-100 text-blue-800 border-blue-200",
      forwarded_to_coordinator:
        "bg-purple-100 text-purple-800 border-purple-200",
      completed: "bg-green-100 text-green-800 border-green-200",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {IncidentStatus[status as keyof typeof IncidentStatus] || status}
    </span>
  );
};

const IncidentList: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [activeTab, setActiveTab] = useState("All");
  const [incidents, setIncidents] = useState<CrimeReference[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();

  // Map the active status tab (label) to the backend `status` filter value.
  const statusParam =
    STATUS_FILTERS.find((f) => f.label === activeTab)?.value ?? undefined;

  const fetchData = (page = currentPage) => {
    setLoading(true);

    // Corruption director/coordinator, screening coordinator & registrar only
    // see their admin-unit's incidents via the by-coordinator-role endpoint.
    const isCorruptionDirector = hasRole(loginData?.roles, [
      Roles.corruptionDirector,
    ]);
    const isCorruptionCoordinator = hasRole(loginData?.roles, [
      Roles.corruptionCoordinator,
    ]);
    const isScreeningCoordinator = hasRole(loginData?.roles, [
      Roles.screeningTeamCoordinator,
    ]);
    // The economic/corruption registrar names are swapped in types.ts, so match
    // both string variants to reliably catch CaseCorruptionRegistrar.
    const isCorruptionRegistrar = hasRole(loginData?.roles, [
      Roles.economicRegistrar, // "CaseCorruptionRegistrar"
      Roles.corruptionRegistrar, // "CaseEconomicRegistrar"
    ]);

    const coordinatorRole = isCorruptionDirector
      ? Roles.corruptionDirector
      : isCorruptionCoordinator
        ? Roles.corruptionCoordinator
        : isScreeningCoordinator
          ? Roles.screeningTeamCoordinator
          : isCorruptionRegistrar
            ? Roles.economicRegistrar // "CaseCorruptionRegistrar"
            : null;

    const params = {
      page,
      pageSize: ITEMS_PER_PAGE,
      search: debouncedSearch || undefined,
      status: statusParam || undefined,
    };

    const api = coordinatorRole
      ? fetchCrimeReferencesByCoordinatorRolePaginated(
          loginData?.admin_unit || "",
          coordinatorRole,
          params,
        )
      : fetchCrimeReferencesPaginated(params);

    api
      .then((data) => {
        setIncidents(data.results || []);
        setCount(data.count || 0);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setIncidents([]);
        setCount(0);
        setLoading(false);
      });
  };

  // Debounce the search box; reset to the first page on a new term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Refetch whenever login, page, search term, or status tab changes.
  useEffect(() => {
    if (loginData) fetchData(currentPage);
  }, [loginData, currentPage, debouncedSearch, activeTab]);

  const totalPages = Math.ceil(count / ITEMS_PER_PAGE);

  const canRegisterIncident = hasRole(loginData?.roles, [
    Roles.economicDirector,
    Roles.screeningTeamCoordinator,
    Roles.corruptionDirector,
  ]);

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title="Incident Request"
        icon={FaBriefcase}
        tabs={tabs}
        activeTab={activeTab}
        onTabClick={(tab: string) => {
          setActiveTab(tab);
          setCurrentPage(1);
        }}
        rightContent={
          <>
            {/* <input
              type="date"
              className="px-3 py-2 text-sm text-gray-600 border rounded"
              placeholder="Filter by Date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            /> */}
            {canRegisterIncident && (
              <button
                onClick={() => navigate("/incident-register")}
                className="px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700"
              >
                Register Incident
              </button>
            )}
          </>
        }
      />
      {/* {activeTab === "All" ? (
        <AllTab incidents={filteredIncidents} />
      ) : activeTab === "Approved" ? (
        <ApprovedTab />
      ) : activeTab === "Cancelled" ? (
        <CancelledTab />
      ) : null} */}

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label="Loading Incidents" />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={5} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      {/* Left: Back + Title */}
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title="Go Back"
                        >
                          ◀️
                        </button>
                        <span>Incidents List Table</span>
                      </div>

                      {/* Right: Search Box */}
                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search"
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">Incident Reg No</th>
                  <th className="p-3 text-left">Incident Report Date</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Crime Type</th>
                  <th className="p-3 text-left" rounded-tr-lg>
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((incident, idx) => (
                  <tr
                    key={incident?.uu_id ?? idx}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {incident?.Incident_Registration_number}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {DateView(incident.date_received)}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge status={incident?.status || ""} />
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {incident?.crime_type
                        ? CrimeType[incident?.crime_type]
                        : ""}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            hasRole(loginData?.roles, [Roles.clerk])
                              ? "/record-incident/" + incident?.uu_id
                              : "/incident/" + incident?.uu_id,
                          )
                        }
                        className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && incidents.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-gray-500">
                      No incidents found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentList;
